<!--suppress ALL -->
<template>
    <div v-show="!submitted" id="login-form" class="h-100" style="min-height: auto !important">
        <form
            :key="'form' + $i18n.locale"
            ref="loginForm"
            autocomplete="off"
            @submit.prevent="connect"
            @keydown.enter.prevent="sendButtonClickHandler"
        >
            <CustomLoginForm
                v-if="customLoginFormEnabled"
                :subjects="subjects"
                :last-login-data="lastLoginData"
                :get-values="getCustomLoginFormValues"
            />
            <div v-else>
                <md-field
                    :class="{
                        'md-invalid': errors.items.find((x) => x.field == $t('message.yourName')) !== undefined,
                    }"
                >
                    <label>{{ $t("message.yourName") }}</label>
                    <md-input v-model="form.fullname" v-validate="'required'" :name="$t('message.yourName')" />
                    <span class="md-error">
                        <div class="custom-md-error">
                            <a-icon
                                :v-if="errors.items.find((x) => x.field == $t('message.yourName'))"
                                class="mr-2 aIcon"
                                type="exclamation-circle"
                            />
                            {{
                                errors.items.find((x) => x.field == $t("message.yourName"))
                                    ? errors.items.find((x) => x.field == $t("message.yourName")).msg
                                    : ""
                            }}
                        </div>
                    </span>
                </md-field>

                <md-field
                    :class="{
                        'md-invalid': errors.items.find((x) => x.field == $t('message.email')) !== undefined,
                    }"
                >
                    <label>{{ $t("message.yourEmail") }}</label>
                    <md-input
                        v-model.trim="form.email"
                        v-validate="'required|email'"
                        :name="$t('message.email')"
                        autocomplete="off"
                        data-vv-validate-on="blur"
                        type="text"
                    />
                    <span class="md-error">
                        <div class="custom-md-error">
                            <a-icon
                                :v-if="errors.items.find((x) => x.field == $t('message.email'))"
                                class="mr-2 aIcon"
                                type="exclamation-circle"
                            />
                            {{
                                errors.items.find((x) => x.field == $t("message.email"))
                                    ? errors.items.find((x) => x.field == $t("message.email")).msg
                                    : ""
                            }}
                        </div>
                    </span>
                </md-field>

                <div v-if="departmentSelectEnabled()" class="subject-select-elem">
                    <md-autocomplete
                        v-model="selectedSubject"
                        v-validate="'required'"
                        :class="{
                            'custom-mdac': true,
                        }"
                        :md-options="subjects.map((type) => type.label)"
                        :name="$t('message.subject')"
                        class="custom-mdac"
                    >
                        <label>{{ $t("message.selectTopic") }}</label>
                    </md-autocomplete>
                </div>
            </div>
            <div class="button-container">
               <div v-if="languageIsOpen() && this.settings.languageShow" @click="openLangModal" style="margin: 10px">
                   <i data-v-05fc5896="" class="md-icon md-icon-font sendIcon md-theme-default" style="color: var(--form-input-label); cursor: pointer;">language</i>
                   <span style="color: var(--form-input-label);"> {{ $t('message.selectChatLang') }}</span>
               </div>
                <Button
                    border-radius="30px"
                    @btnClicked="sendButtonClickHandler"
                    :active="!visitorIsLogging || !reconnecting || !connectingSocket"
                    bg-color="var(--button-color)"
                    padding-y="8px"
                    size="large"
                >
                    <div v-if="visitorIsLogging" class="loader-3 center"><span></span></div>
                    <md-icon v-else style="color: var(--background-color-first)" class="sendIcon">send</md-icon>
                </Button>
            </div>
        </form>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import request from "../../utils/request";
import LoginUser from "../svg/LoginUser";
import MailForm from "../svg/MailForm";
import CustomLoginForm from "./CustomLoginForm";
import { log2 } from "../../utils/log2";
import { getPersistenceData, removePersistenceData, setPersistenceData } from "../../utils/persistenceStorage";
import { filter } from "rxjs/operators";
import { publishMessage, systemMessageStream, SystemMessageTypes } from "../../utils/systemPubSub";
import * as _ from "lodash";
import { sessionId } from "../../utils/session";
import Button from "../../common/Button.vue";

const log = log2("LoginForm");
const getLastLoginData = () => JSON.parse(getPersistenceData("lastLoginData") || null);
export default {
    name: "LoginForm",
    components: {
        Button,
        LoginUser,
        MailForm,
        CustomLoginForm,
    },
    props: ["propConnect"],

    data() {
        return {
            subjects: [],
            form: {
                email: _.get(getLastLoginData(), "email"),
                fullname: _.get(getLastLoginData(), "fullname"),
                subjectId: undefined,
            },
            lastLoginData: getLastLoginData(),
            lastCustomLogins: JSON.parse(getPersistenceData("lastCustomLogins") || "[]"),
            selectedUser: undefined,
            customFormValues: undefined,
            submitted: false,
            selectedSubject: null,
            formVisible: false,
        };
    },
    computed: {
        ...mapState({
            visitorId: (state) => state.chat.visitorId,
            visitorIsLogging: (state) => state.chat.visitorIsLogging,
            visitorIsLoggedIn: (state) => state.chat.visitorIsLoggedIn,
            mouseUpOnWindow: (state) => state.app.mouseUpOnWindow,
            site: (state) => state.site.site,
            language: (state) => state.language.language,
            settings: (state) => state.setting.settings,
            preSelectedDepartment: (state) => state.chat.preSelectedDepartment,
            contactPropertyData: (state) => state.chat.contactPropertyData,
            currentHostUrl: (state) => state.app.currentHostUrl,
            customLoginFormEnabled: (state) => {
                const enabledFlag = state.setting.settings.customLoginFormEnabled;
                const isValid = !!state.customLoginForm.inputs.filter((x) => x.visible).length;
                return enabledFlag && isValid;
            },
            inputs: (state) => state.customLoginForm.inputs,
            preDefinedUserData: (state) => state.chat.preDefinedUserData,
            visitingPage: (state) => state.app.visitingPage,
        }),
        ...mapGetters({
            mouseUpOnWindow: "chat/mouseUpOnWindow",
            reconnecting: "chat/reconnecting",
            connectingSocket: "chat/connectingSocket",
        }),
    },
    created() {
        log.debug("created");
        if (this.site && this.site._id) {
            this.getSubjects().then((x) => {
                this.subjects = x;
                log("getSubjects worked ", this.subjects);
            });
        }
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    beforeDestroy() {
        if (this.sub1) this.sub1.unsubscribe();
    },
    mounted() {
        setTimeout(() => {
            log.debug("mounted", {
                isLogin: this.visitorIsLogging,
                reco: this.reconnecting,
                sock: this.connectingSocket,
            });
        }, 5000);
        log("visitorId", this.visitorId);
        this.sub1 = systemMessageStream
            .pipe(filter((x) => x.type === SystemMessageTypes.PERSONAL_DATA_DELETED))
            .subscribe(() => {
                removePersistenceData("lastLogins");
                removePersistenceData("lastCustomLogins");
                this.lastLoginData = null;
                this.lastCustomLogins = [];
            });
        if (!this.customLoginFormEnabled && this.preDefinedUserData && this.preDefinedUserData.initialized) {
            const { name, email, phone } = this.preDefinedUserData;
            this.form = {
                ...this.form,
                fullname: name ? name : this.form.fullname,
                email: email ? email : this.form.email,
            };
        }
        if (this.preSelectedDepartment) {
            this.applyPreSelectedDepartment(this.preSelectedDepartment);
        }
    },

    methods: {
        ...mapActions({
            setVisitorIsLogging: "chat/setVisitorIsLogging",
            setVisitorIsLoggedIn: "chat/setVisitorIsLoggedIn",
        }),
        ...mapMutations({
            changePageIndex: "app/CHANGE_PAGE",
        }),
        sendButtonClickHandler() {
            this.connect();
        },
        getCustomLoginFormValues(cb) {
            log("getCustomLoginFormValues called", cb);
            this.getCustomLoginFormValuesCallback = cb;
        },
        applyPreSelectedDepartment(department) {
            const maybeSubject = this.subjects.find((x) => x.label === department || x.id === department);
            log("applyPreSelectedDepartment", { subjects: this.subjects, department });
            if (maybeSubject) {
                this.selectedSubject = maybeSubject.label;
            }
        },
        languageIsOpen() {
            return this.inputs.find((x) => x.type === "language");
        },
        connect() {
            log.debug("connect called");
            const isSocketConnecting = this.connectingSocket || this.reconnecting;
            if (this.getCustomLoginFormValuesCallback) {
                const { values, validator } = this.getCustomLoginFormValuesCallback();
                log("getCustomLoginFormValuesCallback datas", { values, validator });
                this.customFormValidator = validator;
                this.customFormValues = values;
            }
            let validator = this.customLoginFormEnabled ? this.customFormValidator : this.$validator;
            validator = validator
                ? validator
                : {
                      validateAll: () => Promise.resolve(false),
                  };
            try {
                validator
                    .validateAll()
                    .then((isSuccess) => {
                        if (isSuccess) {
                            if (!isSocketConnecting && !this.visitorIsLogging) {
                                if (this.site && this.site._id != null) {
                                    if (this.customLoginFormEnabled) {
                                        const payload = {
                                            visitorId: this.visitorId,
                                            languageCode: this.language.code,
                                            siteId: this.site._id,
                                            customLoginForm: true,
                                            customLoginFormValues: this.customFormValues,
                                            sessionId,
                                            visitingPage: this.visitingPage,
                                        };

                                        publishMessage(SystemMessageTypes.SEND_LOGIN, payload);
                                        var formVals = this.inputs.reduce((acc, input) => {
                                            let maybeVal = this.customFormValues.find(
                                                (vals) => vals.input === input._id
                                            );
                                            acc[input.type] = maybeVal ? maybeVal.value : null;
                                            return acc;
                                        }, {});

                                        formVals.customLoginFormValues = this.customFormValues;
                                        formVals.version = this.settings.customLoginVersion;

                                        this.lastCustomLogins = [formVals];
                                    } else {
                                        const payload = {
                                            visitorId: this.visitorId,
                                            email: this.form.email,
                                            fullname: this.form.fullname,
                                            subjectId: this.form.subjectId,
                                            languageCode: this.language.code,
                                            siteId: this.site._id,
                                            visitingPage: this.visitingPage,
                                            sessionId,
                                        };

                                        publishMessage(SystemMessageTypes.SEND_LOGIN, payload);
                                        this.lastLoginData = this.form;
                                    }
                                    this.submitted = true;
                                }
                            }
                        } else {
                            const result = validator.errors.items[0].field;
                            const errorInput = document.querySelector(`[name="${result}"]`);
                            errorInput.focus();
                            errorInput.scrollIntoView({ block: "center" });
                            log.warn("errors", this.errors);
                        }
                    })
                    .catch((err) => {
                        log.warn("validator error", err);
                    });
            } catch (e) {
                log.warn("validator error", e);
            }
        },
        conectWithForm(user, index) {
            const isSocketConnecting = this.connectingSocket || this.reconnecting;
            if (!isSocketConnecting && !this.visitorIsLogging) {
                try {
                    this.$validator
                        .validateAll()
                        .then((isSuccess) => {
                            if (isSuccess) {
                                if (this.site && this.site._id != null) {
                                    const payload = {
                                        ...user,
                                        visitorId: this.visitorId,
                                        email: user.email,
                                        fullname: user.fullname,
                                        subjectId: user.subjectId,
                                        languageCode: this.language.code,
                                        siteId: this.site._id,
                                        visitingPage: this.visitingPage,
                                        customLoginForm: this.customLoginFormEnabled,
                                        sessionId,
                                    };

                                    this.selectedUser = index;
                                    publishMessage(SystemMessageTypes.SEND_LOGIN, payload);
                                }
                            } else {
                                log("errors", this.errors);
                            }
                        })
                        .catch((err) => {
                            log.warn("validator error", err);
                        });
                } catch (e) {
                    log.warn("validator error", e);
                }
            }
        },
        async getSubjects() {
            const { data } = await request.get("/public/subjects/get-all");
            const subjects = data.map((sub) => {
                const id = sub._id;
                let label = "";
                const maybeLabel = sub.languageContent.find(
                    (x) => x.languageCode === this.settings.language.customerClientDefault
                );

                if (maybeLabel) {
                    log("maybeLabel", maybeLabel);
                    label = maybeLabel.name;
                }
                return {
                    id,
                    label,
                };
            });
            return subjects;
        },
        changeTicketMode() {
            this.$emit("change-to-ticket", true);
        },
        departmentSelectEnabled() {
            if (this.preSelectedDepartment) {
                return !this.subjects.find((x) => x.label === this.preSelectedDepartment);
            } else {
                return this.settings.subject && this.subjects.length;
            }
        },
        openLangModal() {
            this.changePageIndex(4)
        },
    },
    watch: {
        lastLoginData() {
            setPersistenceData("lastLoginData", JSON.stringify(this.lastLoginData), true);
        },
        lastCustomLogins() {
            setPersistenceData("lastCustomLogins", JSON.stringify(this.lastCustomLogins), true);
        },
        site(newValue, oldValue) {
            if (newValue) {
                this.getSubjects().then((x) => {
                    this.subjects = x;
                    log("getSubjects worked ", this.subjects);
                });
            }

            if (this.socketConnected) {
                const payload = {
                    visitorId: this.visitorId,
                    email: this.form.email,
                    fullname: this.form.fullname,
                    subjectId: this.form.subjectId,
                    languageCode: this.language.code,
                    siteId: this.site._id,
                    visitingPage: this.visitingPage,
                    sessionId,
                };

                publishMessage(SystemMessageTypes.SEND_LOGIN, payload);
            }
        },
        settings(newSettings) {
            if (newSettings) {
                log("settings updated", { newSettings, subjects: this.settings.subject });
                this.lastCustomLogins = this.lastCustomLogins.filter(
                    (x) => x.version === newSettings.customLoginVersion
                );
                this.$forceUpdate();
            }
        },
        visitorIsLoggedIn(newValue) {
            if (newValue !== undefined && newValue === true) {
                const formData = {
                    email: undefined,
                    fullname: undefined,
                    subjectId: undefined,
                };
                if (!this.customLoginFormEnabled && this.preDefinedUserData && this.preDefinedUserData.initialized) {
                    const { name, email, phone } = this.preDefinedUserData;
                    if (name) formData.fullname = name;
                    if (email) formData.email = email;
                }
                this.form = formData;
                this.selectedUser = undefined;
            }
        },
        selectedSubject(newVal) {
            const maybeSubject = this.subjects.find((x) => x.label === newVal);
            if (maybeSubject) {
                this.form.subjectId = maybeSubject.id;
            } else {
                this.form.subjectId = undefined;
            }
        },
        preDefinedUserData(val) {
            if (!val) return;
            if (!val.initialized) return;
            if (!this.customLoginFormEnabled) {
                const { name, email, phone } = val;
                if (name) this.form.fullname = name;
                if (email) this.form.email = email;
            }
        },
        preSelectedDepartment(department) {
            this.applyPreSelectedDepartment(department);
        },
    },
};
</script>

<style lang="scss" src="../../assets/scss/components/v2/LoginForm.scss" />



// WEBPACK FOOTER //
// src/components/V2/LoginForm.vue